import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { 
  Mail, 
  UserX, 
  Newspaper, 
  Bell, 
  Play, 
  CheckCircle, 
  XCircle,
  Clock,
  RefreshCw
} from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useTriggerEmailJob, EMAIL_JOBS } from '@/hooks/useTriggerEmailJob';

const iconMap: Record<string, any> = {
  'mail': Mail,
  'user-x': UserX,
  'newspaper': Newspaper,
  'bell': Bell,
};

const EmailAutomationsPanel = () => {
  const { triggerJob, isLoading, getLastResult } = useTriggerEmailJob();

  // Fetch last execution logs for each job
  const { data: jobLogs, isLoading: logsLoading, refetch: refetchLogs } = useQuery({
    queryKey: ['admin-cron-job-logs'],
    queryFn: async () => {
      const logs: Record<string, any> = {};
      
      for (const job of EMAIL_JOBS) {
        const { data, error } = await supabase
          .from('cron_job_logs')
          .select('*')
          .eq('job_name', job.functionName)
          .order('executed_at', { ascending: false })
          .limit(1)
          .maybeSingle();

        if (!error && data) {
          logs[job.functionName] = data;
        }
      }

      return logs;
    },
    refetchInterval: 30000, // Refresh every 30 seconds
  });

  // Fetch pending email stats
  const { data: pendingStats } = useQuery({
    queryKey: ['admin-pending-emails-stats'],
    queryFn: async () => {
      const { count: pendingCount } = await supabase
        .from('scheduled_emails')
        .select('*', { count: 'exact', head: true })
        .eq('status', 'pending');

      const { count: processingCount } = await supabase
        .from('scheduled_emails')
        .select('*', { count: 'exact', head: true })
        .eq('status', 'processing');

      const { count: failedCount } = await supabase
        .from('scheduled_emails')
        .select('*', { count: 'exact', head: true })
        .eq('status', 'failed');

      return {
        pending: pendingCount || 0,
        processing: processingCount || 0,
        failed: failedCount || 0,
      };
    },
    refetchInterval: 10000,
  });

  const handleTrigger = async (functionName: string) => {
    await triggerJob(functionName);
    refetchLogs();
  };

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return 'Nunca executado';
    return new Date(dateStr).toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <div className="space-y-6">
      {/* Queue Status */}
      <Card className="bg-white/5 backdrop-blur-sm border-white/10">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <Mail className="h-5 w-5 text-luxury-pinkBright" />
            Status da Fila de Emails
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-3 gap-4">
            <div className="text-center p-4 bg-white/5 rounded-lg">
              <p className="text-white/60 text-sm">Pendentes</p>
              <p className="text-2xl font-bold text-yellow-400">{pendingStats?.pending || 0}</p>
            </div>
            <div className="text-center p-4 bg-white/5 rounded-lg">
              <p className="text-white/60 text-sm">Processando</p>
              <p className="text-2xl font-bold text-blue-400">{pendingStats?.processing || 0}</p>
            </div>
            <div className="text-center p-4 bg-white/5 rounded-lg">
              <p className="text-white/60 text-sm">Falharam</p>
              <p className="text-2xl font-bold text-red-400">{pendingStats?.failed || 0}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Job Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {EMAIL_JOBS.map((job) => {
          const Icon = iconMap[job.icon] || Mail;
          const lastLog = jobLogs?.[job.functionName];
          const currentResult = getLastResult(job.functionName);
          const loading = isLoading(job.functionName);

          return (
            <Card 
              key={job.functionName} 
              className="bg-white/5 backdrop-blur-sm border-white/10 hover:bg-white/10 transition-all"
            >
              <CardContent className="p-4">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-luxury-pinkBright/20 rounded-lg">
                      <Icon className="h-5 w-5 text-luxury-pinkBright" />
                    </div>
                    <div>
                      <h3 className="text-white font-medium">{job.name}</h3>
                      <p className="text-white/60 text-xs">{job.description}</p>
                    </div>
                  </div>
                </div>

                {/* Last Execution Info */}
                <div className="space-y-2 mb-4">
                  <div className="flex items-center gap-2 text-xs">
                    <Clock className="h-3 w-3 text-white/40" />
                    <span className="text-white/60">
                      Última execução: {logsLoading ? <Skeleton className="inline-block w-24 h-3 bg-white/10" /> : formatDate(lastLog?.executed_at)}
                    </span>
                  </div>
                  
                  {lastLog && (
                    <div className="flex items-center gap-2">
                      {lastLog.success ? (
                        <Badge className="bg-green-500/20 text-green-400 text-xs">
                          <CheckCircle className="h-3 w-3 mr-1" />
                          Sucesso ({lastLog.records_affected || 0} registros)
                        </Badge>
                      ) : (
                        <Badge className="bg-red-500/20 text-red-400 text-xs">
                          <XCircle className="h-3 w-3 mr-1" />
                          Falhou
                        </Badge>
                      )}
                    </div>
                  )}

                  {currentResult && !loading && (
                    <div className="flex items-center gap-2">
                      {currentResult.success ? (
                        <Badge className="bg-blue-500/20 text-blue-400 text-xs">
                          Agora: {currentResult.scheduled || currentResult.processed || currentResult.sent || 0} processados
                        </Badge>
                      ) : (
                        <Badge className="bg-red-500/20 text-red-400 text-xs">
                          Erro: {currentResult.error}
                        </Badge>
                      )}
                    </div>
                  )}
                </div>

                {/* Trigger Button */}
                <Button
                  onClick={() => handleTrigger(job.functionName)}
                  disabled={loading}
                  variant="brand-gradient"
                  className="w-full"
                  size="sm"
                >
                  {loading ? (
                    <>
                      <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                      Executando...
                    </>
                  ) : (
                    <>
                      <Play className="h-4 w-4 mr-2" />
                      Executar Agora
                    </>
                  )}
                </Button>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Instructions */}
      <Card className="bg-white/5 backdrop-blur-sm border-white/10">
        <CardContent className="p-4">
          <p className="text-white/60 text-sm">
            <strong className="text-white">Nota:</strong> Estas automações são normalmente executadas por cron jobs a cada 5 minutos.
            Use os botões acima para disparar manualmente quando necessário.
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

export default EmailAutomationsPanel;
